"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSOLAFloating } from "./context";
import { FloatingTrigger } from "./FloatingTrigger";
import { FloatingWindow } from "./FloatingWindow";

export function FloatingContainer() {
  const {
    state,
    setPosition,
    setSize,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
  } = useSOLAFloating();

  const [mounted, setMounted] = useState(false);
  const constraintsRef = useRef(null);
  const windowRef = useRef(null);

  // Ensure we're on the client side before rendering the document.body portal.
  // Intentional: client-only mount flag to avoid an SSR hydration mismatch.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
  }, []);

  // Calculate actual position from offset
  const getActualPosition = () => {
    if (typeof window === "undefined") return { x: 0, y: 0 };

    const { x, y } = state.position;
    const { width, height } = state.size;

    // If position is negative, it's an offset from bottom-right
    const actualX = x < 0 ? window.innerWidth + x - width : x;
    const actualY = y < 0 ? window.innerHeight + y - height : y;

    return { x: actualX, y: actualY };
  };

  // Constrain position to viewport
  const constrainPosition = (x, y) => {
    if (typeof window === "undefined") return { x, y };

    const { width, height } = state.size;
    const maxX = window.innerWidth - width;
    const maxY = window.innerHeight - height;

    return {
      x: Math.max(0, Math.min(maxX, x)),
      y: Math.max(0, Math.min(maxY, y)),
    };
  };

  // No drag handling needed for fixed position

  // Handle window resize
  useEffect(() => {
    // Guard against SSR
    if (typeof window === "undefined") return;

    const handleResize = () => {
      // Reposition if window goes outside viewport
      const actualPos = getActualPosition();
      const constrained = constrainPosition(actualPos.x, actualPos.y);

      if (constrained.x !== actualPos.x || constrained.y !== actualPos.y) {
        setPosition(constrained);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [state.position, state.size, setPosition]);

  // Window dimensions based on state
  const getWindowDimensions = () => {
    if (typeof window === "undefined") return { width: 0, height: 0, x: 0, y: 0 };

    if (state.isMaximized) {
      const maxWidth = Math.min(1200, window.innerWidth - 20);
      const maxHeight = Math.min(900, window.innerHeight - 80);
      return {
        width: maxWidth,
        height: maxHeight,
        x: 0,
        y: 0,
      };
    }

    const actualPos = getActualPosition();
    return {
      width: state.size.width,
      height: state.size.height,
      x: actualPos.x,
      y: actualPos.y,
    };
  };

  // Don't render on server
  if (!mounted) return null;

  return createPortal(
    <>
      {/* Viewport constraints for dragging */}
      <div
        ref={constraintsRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 1249,
        }}
      />

      {/* Floating trigger button */}
      <AnimatePresence>
        {(!state.isOpen || state.isMinimized) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed",
              bottom: 20,
              right: 20,
              zIndex: 1250,
              pointerEvents: "auto",
            }}
          >
            <FloatingTrigger
              onClick={state.isMinimized ? restoreWindow : openWindow}
              hasNotifications={state.notifications.some((n) => !n.read)}
              notificationCount={
                state.notifications.filter((n) => !n.read).length
              }
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating window */}
      <AnimatePresence>
        {state.isOpen && !state.isMinimized && (
          <motion.div
            ref={windowRef}
            initial={{
              opacity: 0,
              scale: 0.9,
            }}
            animate={{
              opacity: state.preferences.opacity,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              scale: 0.9,
              transition: { duration: 0.2 },
            }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 300,
              duration:
                state.preferences.animationSpeed === "fast"
                  ? 0.15
                  : state.preferences.animationSpeed === "slow"
                  ? 0.4
                  : 0.25,
            }}
            style={{
              position: "fixed",
              ...(state.isMaximized
                ? { top: 0, left: 0, right: 0, bottom: 0 }
                : { bottom: 80, right: 20, width: state.size.width, height: state.size.height }),
              zIndex: 1250,
              pointerEvents: "auto",
              transformOrigin: "bottom right",
            }}
          >
            <FloatingWindow
              onClose={closeWindow}
              onMinimize={minimizeWindow}
              onMaximize={state.isMaximized ? restoreWindow : maximizeWindow}
              onResize={(newSize) => setSize(newSize)}
              isMaximized={state.isMaximized}
              isDragging={state.isDragging}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Maximized overlay backdrop */}
      <AnimatePresence>
        {state.isOpen && state.isMaximized && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1249,
              pointerEvents: "auto",
            }}
            onClick={restoreWindow}
          />
        )}
      </AnimatePresence>
    </>,
    document.body
  );
}

// Responsive breakpoint utility
export function useResponsiveBreakpoint() {
  const [breakpoint, setBreakpoint] = useState("desktop");

  useEffect(() => {
    // Guard against SSR
    if (typeof window === "undefined") return;

    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setBreakpoint("mobile");
      } else if (width <= 1024) {
        setBreakpoint("tablet");
      } else {
        setBreakpoint("desktop");
      }
    };

    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, []);

  return breakpoint;
}

// Position utilities (fixed bottom-right)
export const positionPresets = {
  "bottom-right": { x: -420, y: -620 },
};

export const sizePresets = {
  compact: { width: 320, height: 480 },
  normal: { width: 400, height: 600 },
  large: { width: 480, height: 720 },
};
